import { useState } from "react";
import useMediaQuery from "@repo/shared-ui/useMediaQuery";
import BFLLink from "@repo/shared-ui/BFLLink";

interface ProductProps {
  data: Record<string, any>[];
}

function groupProductColumns(data: any[]): any[][] {
  const columns: any[][] = [];
  const groupings = [
    ["Loans"],
    ["Insurance", "Finance for Professionals"],
    ["Investments", "Pocket Subscription"],
    ["Bajaj Mall", "Services"],
    ["Wallets & Cards", "Value Added Services", "Payments"],
    // Each special section in its own group for better control
    ["Pre-approved Offers"],
    ["Offer World"],
    ["Article and Insights"],
  ];
  let i = 0;
  while (i < data.length) {
    let matched = false;
    for (const group of groupings) {
      if (group.every((title, idx) => data[i + idx]?.sectionTitle === title)) {
        columns.push(group.map((_, idx) => data[i + idx]));
        i += group.length;
        matched = true;
        break;
      }
    }
    if (!matched) {
      columns.push([data[i]]);
      i++;
    }
  }
  return columns;
}

const Products: React.FC<ProductProps> = ({ data }) => {
  const columns = groupProductColumns(data);
  // By default, all accordions are closed (openMobile is null)
  const [openMobile, setOpenMobile] = useState<string | null>(null);
  // Use a media query hook for mobile detection
  const isMobile = useMediaQuery("(max-width: 767px)");

  // Filter out special sections
  const specialSections = data.filter((section) =>
    ["Pre-approved Offers", "Offer World", "Article and Insights"].includes(section.sectionTitle)
  );

  // Filter columns to exclude special sections
  const regularColumns = columns.filter(
    (sections) =>
      !sections.some((section) =>
        ["Pre-approved Offers", "Offer World", "Article and Insights"].includes(
          section.sectionTitle
        )
      )
  );

  return (
    <div className="w-full">
      <div className="grid grid-cols-1 md:grid-cols-6 md:gap-8">
        {/* Regular columns */}
        {regularColumns.map((sections, colIdx) => {
          // Create a stable key for each column based on section titles
          const colKey = sections.map((s: any) => s.sectionTitle).join("-") || colIdx;

          return (
            <div key={colKey}>
              {sections.map((section: any, sidx: number) => {
                const isOpen = openMobile === section.sectionTitle;

                // For special sections, use compact spacing
                const isSpecialSection = [
                  "Pre-approved Offers",
                  "Offer World",
                  "Article and Insights",
                ].includes(section.sectionTitle);

                // Extract className logic to a variable for clarity
                const sectionClassName = isSpecialSection ? "mt-4" : sidx > 0 ? "mt-4" : "";

                return (
                  <div key={section.sectionTitle || sidx} className={sectionClassName}>
                    {/* Special handling for sections with no links */}
                    {["Pre-approved Offers", "Offer World", "Article and Insights"].includes(
                      section.sectionTitle
                    ) ? (
                      <h3 className="mb-2 text-sm leading-[24px]">
                        <BFLLink
                          href={
                            section.sectionTitle === "Pre-approved Offers"
                              ? "/pre-approved-offers"
                              : section.sectionTitle === "Offer World"
                                ? "/offers"
                                : "/blogs"
                          }
                          className="text-sm text-white"
                        >
                          {section.sectionTitle}
                        </BFLLink>
                      </h3>
                    ) : (
                      <>
                        <h3 className="mb-4 text-sm leading-[24px] font-medium">
                          <button
                            type="button"
                            className="w-full text-left md:pointer-events-none md:cursor-default"
                            onClick={() => {
                              if (window.innerWidth < 768) {
                                setOpenMobile(isOpen ? null : section.sectionTitle);
                              }
                            }}
                          >
                            {section.sectionTitle}
                            <span className="float-right inline-block md:hidden">
                              <i
                                className={`icomoon transition-transform before:content-['\\e909'] ${isOpen ? "rotate-180" : ""}`}
                              ></i>
                            </span>
                          </button>
                        </h3>
                        <ul className={isMobile && !isOpen ? "hidden" : ""}>
                          {Array.isArray(section.links)
                            ? section.links.map((link: any, linkIdx: number) => (
                                <li
                                  className="my-1 leading-[18.2px]"
                                  key={link.linkTitle || linkIdx}
                                >
                                  <BFLLink
                                    href={link.linkUrl || "#"}
                                    className="text-[10px] leading-[19.2px] text-white"
                                  >
                                    {link.linkTitle}
                                  </BFLLink>
                                </li>
                              ))
                            : null}
                        </ul>
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          );
        })}
        {/* Special sections in the last column */}
        <div className="md:col-start-6">
          {specialSections.map((section, index) => (
            <div key={section.sectionTitle} className={index > 0 ? "mt-3" : ""}>
              <h3 className="mb-1 text-sm leading-[24px]">
                <BFLLink
                  href={
                    section.sectionTitle === "Pre-approved Offers"
                      ? "/pre-approved-offers"
                      : section.sectionTitle === "Offer World"
                        ? "/offers"
                        : "/blogs"
                  }
                  className="text-sm text-white"
                >
                  {section.sectionTitle}
                </BFLLink>
              </h3>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Products;
